import { io, Socket } from "socket.io-client";
import { Config } from "@eagerdog/config";

import { localStorageService } from "./local-storage.service";

export interface ScoreUpdatedData {
  showId: string;
  scoreId: string;
  timestamp: string;
}

/**
 * Web client for the `/scores` socket namespace (room `score_show_${showId}`,
 * event `score_updated`). Mirrors the mobile `scoreWebSocket.service.ts`.
 * The existing `websocket.service.ts` singleton is bound to `/run-order`, so
 * score-level updates get their own connection.
 */
class ScoreSocketService {
  private socket: Socket | null = null;
  private joinedRooms = new Set<string>();
  private callbacks = new Set<(data: ScoreUpdatedData) => void>();

  private ensureConnected(): void {
    if (this.socket) return;
    const token = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
    if (!token) return;
    const baseUrl = Config.web.REACT_APP_API_URL || "http://localhost:3001";
    this.socket = io(`${baseUrl}/scores`, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    this.socket.on("connect", () => {
      this.joinedRooms.forEach((showId) => this.socket?.emit("join_score_room", { showId }));
    });
    this.socket.on("score_updated", (data: ScoreUpdatedData) => {
      this.callbacks.forEach((cb) => cb(data));
    });
  }

  joinShowRoom(showId: string): void {
    this.ensureConnected();
    this.joinedRooms.add(showId);
    if (this.socket?.connected) this.socket.emit("join_score_room", { showId });
  }

  leaveShowRoom(showId: string): void {
    this.joinedRooms.delete(showId);
    if (this.socket?.connected) this.socket.emit("leave_score_room", { showId });
    if (this.joinedRooms.size === 0 && this.callbacks.size === 0) this.disconnect();
  }

  onScoreUpdated(callback: (data: ScoreUpdatedData) => void): () => void {
    this.callbacks.add(callback);
    return () => {
      this.callbacks.delete(callback);
    };
  }

  disconnect(): void {
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
  }
}

export const scoreSocketService = new ScoreSocketService();
